import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { CommonConstant } from 'src/app/constants';

import {
  BarcodeReaderEventType,
  BarcodeResult,
  BaseMenuItem,
  GiftCard,
  PriceType
} from 'src/app/models';
import { TicketProvider } from 'src/app/providers';
import { BarcodeProvider } from 'src/app/providers/barcode.provider';
import { LogRocketProvider } from 'src/app/providers/logrocket.provider';
import { ModalService } from 'src/app/services';
import {
  GetTopUpGiftCard,
  GetTopUpGiftCardMultiMatch,
  GetTopUpGiftCardNoMatch,
  GetTopUpGiftCardSuccess
} from 'src/app/store/payment/payment.action';
import { TicketsStateSelectors } from 'src/app/store/tickets/tickets.selectors';

enum GiftCardItemModalView {
  LIST = 'list',
  CUSTOM_PRICE = 'custom-price',
  SCAN = 'scan',
  MANUAL = 'manual',
  // POS-1742: shown when the scanned/entered number matches more than one
  // card issued at this facility; operator must pick which one to top up.
  SELECT = 'select',
  SUMMARY = 'summary'
}

@Component({
  selector: 'pos-gift-card-item-modal',
  templateUrl: './gift-card-item.modal.html',
  styleUrls: ['./gift-card-item.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CurrencyPipe]
})
export class GiftCardItemModal implements OnInit, OnDestroy {
  @Input() menuItem: BaseMenuItem;
  @Input() parentUuid?: string;

  view = GiftCardItemModalView.LIST;
  amount: number | string;
  price: number;
  isVariable = false;
  gift_card_code: string;
  gift_card_id: number = null;
  matchingGiftCards: GiftCard[] = [];
  barcodeSubscription: Subscription;
  giftCardLookupSubscription: Subscription;

  constructor(
    private readonly modalController: ModalController,
    private readonly barcodeProvider: BarcodeProvider,
    private readonly ticketProvider: TicketProvider,
    private readonly logRocketProvider: LogRocketProvider,
    private readonly cdr: ChangeDetectorRef,
    private readonly modalService: ModalService,
    private readonly currencyPipe: CurrencyPipe,
    private readonly translocoService: TranslocoService,
    private readonly store: Store,
    private readonly actions$: Actions
  ) {}

  ngOnInit() {
    this.price = this.menuItem.price;
    this.isVariable = this.menuItem.price_type === PriceType.VARIABLE;
    if (!this.isVariable) {
      this.view = GiftCardItemModalView.SCAN;
    }

    this.barcodeSubscription = this.barcodeProvider.subscribe(
      BarcodeReaderEventType.GIFT_CARD,
      (barcode: BarcodeResult) => {
        this.logRocketProvider.log(
          '[Gift Card Item Modal] BarcodeReaderEventType.GIFT_CARD barcode',
          JSON.stringify(barcode)
        );
        if (this.view === GiftCardItemModalView.SCAN) {
          this.resolveGiftCard(barcode.barcode);
        }
      }
    );

    this.giftCardLookupSubscription = new Subscription();
    this.giftCardLookupSubscription.add(
      this.actions$
        .pipe(ofActionSuccessful(GetTopUpGiftCardMultiMatch))
        .subscribe(({ giftCards }) => {
          this.matchingGiftCards = giftCards;
          this.view = GiftCardItemModalView.SELECT;
          this.cdr.markForCheck();
        })
    );
    this.giftCardLookupSubscription.add(
      this.actions$
        .pipe(ofActionSuccessful(GetTopUpGiftCardSuccess))
        .subscribe(({ giftCard }) => {
          this.gift_card_id = +giftCard.id;
          this.view = GiftCardItemModalView.SUMMARY;
          this.cdr.markForCheck();
        })
    );
    this.giftCardLookupSubscription.add(
      this.actions$
        .pipe(ofActionSuccessful(GetTopUpGiftCardNoMatch))
        .subscribe(() => {
          this.gift_card_id = null;
          this.view = GiftCardItemModalView.SUMMARY;
          this.cdr.markForCheck();
        })
    );
  }

  ngOnDestroy() {
    this.barcodeProvider.unsubscribe(this.barcodeSubscription);
    this.giftCardLookupSubscription.unsubscribe();
  }

  onSelectGiftCard(price: number = null) {
    this.price = price;
    this.view = !!price
      ? GiftCardItemModalView.SCAN
      : GiftCardItemModalView.CUSTOM_PRICE;
  }

  onConfirmCustomAmount() {
    const amount = +this.amount,
      MAX_VARIABLE_VALUE = CommonConstant.MAX_VARIABLE_PRICE_INPUT_VALUE;

    if (amount > MAX_VARIABLE_VALUE) {
      void this.modalService
        .alert({
          message: this.translocoService.translate(
            'posManager.invalidDollarAmount',
            {
              maxValue: this.currencyPipe.transform(
                MAX_VARIABLE_VALUE,
                'USD',
                'symbol-narrow',
                '1.0-2'
              )
            }
          ),
          buttons: [this.translocoService.translate('common.ok')]
        })
        .catch((err) => {
          console.error('Error showing alert', err);
        });
      return;
    }

    this.view = GiftCardItemModalView.SCAN;
    this.price = amount;
    this.amount = null;
  }

  onManualGiftCardEntry() {
    this.view = GiftCardItemModalView.MANUAL;
  }

  onConfirmGiftCardNumber() {
    this.resolveGiftCard(this.gift_card_code);
  }

  // POS-1742: same number can exist on more than one card at this facility.
  // Look it up first so we can ask the operator which one to top up instead
  // of silently crediting whichever one the backend happens to pick.
  resolveGiftCard(code: string) {
    this.gift_card_code = code;
    this.gift_card_id = null;

    this.store.dispatch(new GetTopUpGiftCard(code));
  }

  onGiftCardSelected(card: GiftCard) {
    this.gift_card_id = +card.id;
    this.view = GiftCardItemModalView.SUMMARY;
  }

  onAddToTicket() {
    const selectedNormalizedTicket = this.store.selectSnapshot(
      TicketsStateSelectors.normalizedTicket
    );

    this.menuItem.price = this.price;
    this.menuItem.gift_card_code = this.gift_card_code;
    this.menuItem.gift_card_id = this.gift_card_id;

    this.ticketProvider.ticketItemAddingHandler({
      menuItem: this.menuItem,
      ticket_uuid: selectedNormalizedTicket?.device_ticket_uuid,
      ticket_item_uuid: this.parentUuid
    });
    this.modalController.dismiss();
  }

  onBack() {
    if (
      this.view === GiftCardItemModalView.CUSTOM_PRICE ||
      (this.view === GiftCardItemModalView.SCAN && this.isVariable)
    ) {
      this.price = null;
      this.view = GiftCardItemModalView.LIST;
    } else if (
      this.view === GiftCardItemModalView.MANUAL ||
      this.view === GiftCardItemModalView.SUMMARY ||
      this.view === GiftCardItemModalView.SELECT
    ) {
      this.view = GiftCardItemModalView.SCAN;
    }
  }

  onCloseModal() {
    this.modalController.dismiss();
  }
}
